import { useCallback } from 'react';
import { useRequestData } from 'ws-scripts/modules/common';
import trackEvent, {
	TRACK_ACTION,
	TRACK_WIDGET_NAME,
	normalizeDeviceType
} from '../utils/trackEvent';

/**
 * Returns a stable `track(drawerEvent, attributes)` function that fires through
 * the shared `ddc-track-event` pipeline (CR-7), stamping every event with the
 * attributes common to all of them (`action`, `drawerEvent`, `siteId`,
 * `deviceType`) so no call site has to remember them. Call-site attributes win on
 * key collision, which is how each event supplies its own `element`, `result` and
 * `widgetState`.
 *
 * Values come from the SSR `requestData` slice, so they cost no extra request
 * (CR-8). `widgetName` and `windowId` are positional because `ddc-track-event`
 * sets them on the payload itself.
 *
 * Attribute reference: `docs/analytics-event-schema.md` ("Common attributes").
 *
 * @returns {(drawerEvent: string, attributes?: Object) => void}
 */
const useTrackEvent = () => {
	const requestData = useRequestData();
	// `widgetName` is what OSIRIS calls the widget; fall back to our own constant
	// so a missing field degrades to a correctly-named event rather than none.
	// `windowId` has no meaningful fallback — it identifies *which* instance on
	// the page — so `trackEvent` warns and drops rather than inventing one.
	const widgetName = requestData?.widgetName ?? TRACK_WIDGET_NAME;
	const windowId = requestData?.windowId;
	const siteId = requestData?.siteId ?? null;
	const deviceType = normalizeDeviceType(requestData?.deviceType);

	return useCallback(
		(drawerEvent, attributes = {}) =>
			trackEvent(widgetName, windowId, {
				action: TRACK_ACTION,
				drawerEvent,
				siteId,
				deviceType,
				...attributes
			}),
		[widgetName, windowId, siteId, deviceType]
	);
};

export default useTrackEvent;
